/**
 * The mode sheet. One compact mode button opens this; the current mode is
 * clearly marked. Picking a mode switches the visible tools and active layers,
 * so a mode earns its place by hiding what the other jobs need, not by adding.
 *
 * Training is gated behind sign-in, so it does not clutter navigation for
 * someone who just wants a chart. Live Navigation is the default on load: the
 * simulated GNSS feed stands in for real sensors until hardware is connected.
 */
import { useShellStore, type Mode } from '@/store/shell';
import { useRoutingStore, selectActiveRoute } from '@/store/routing';
import { MODES } from './modes';
import { ModeIcon } from './icons';
import { Sheet } from './Sheet';

export function ModeSheet() {
  const mode = useShellStore((s) => s.mode);
  const setMode = useShellStore((s) => s.setMode);
  const openSheet = useShellStore((s) => s.openSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const signedIn = useShellStore((s) => s.signedIn);
  const showToast = useShellStore((s) => s.showToast);

  function pick(next: Mode) {
    if (next === 'Training' && !signedIn) {
      closeSheet();
      showToast('Sign in with an active course to open Training');
      return;
    }
    // Leaving Planning: finish any half-built route so the builder state doesn't
    // linger into a mode that can't reach it.
    const rs = useRoutingStore.getState();
    if (mode === 'Plotting' && next !== 'Plotting' && rs.buildingRouteId) rs.finishRoute();
    setMode(next);
    // Each mode boots into its primary workspace, so the right tools are one
    // step away rather than hidden.
    if (next === 'Plotting') {
      openSheet('planning');
    } else if (next === 'Live') {
      closeSheet();
      // The hand-off: a route built in Planning is already here. If one is ready
      // and not yet being followed, point the way to activating it.
      const route = selectActiveRoute(useRoutingStore.getState());
      const ready = !!route && route.waypoints.length >= 2;
      const following = !!route && useRoutingStore.getState().followRouteId === route.id;
      showToast(ready && !following ? 'Route ready — open Route to follow it' : 'Live Navigation');
    } else {
      closeSheet();
      showToast(`${next} mode`);
    }
  }

  return (
    <Sheet
      name="mode"
      title="Mode"
      sub="Each mode switches off what the other jobs need, so the screen only ever shows the tools for what we are doing now."
    >
      {MODES.map((m) => {
        const active = m.name === mode;
        const locked = (m.gated && !signedIn) || m.preview;
        return (
          <button
            key={m.name}
            type="button"
            className={`acp-mode-row${active ? ' active' : ''}${locked ? ' locked' : ''}`}
            onClick={() => pick(m.name)}
          >
            <span className="mi">
              <ModeIcon mode={m.name} size={22} />
            </span>
            <span className="mt">
              <b>{m.name === 'Training' ? 'Training / Scenario' : m.name === 'Live' ? 'Live Navigation' : m.name}</b>
              <span>{m.blurb}</span>
            </span>
            {active ? (
              <span className="acp-badge tick">✓</span>
            ) : m.preview ? (
              <span className="acp-badge preview">Preview · P3</span>
            ) : (
              <span className="acp-badge">{m.phase}</span>
            )}
          </button>
        );
      })}
    </Sheet>
  );
}
